import type { RapierRigidBody } from '@react-three/rapier';

import { RigidBody } from '@react-three/rapier';
import { useContext, useMemo, useRef } from 'react';
import * as THREE from 'three';

import { ViewContext } from '@/state/contexts';

import {
	playRandomCollision,
	// playRandomCollisionOverlap,
} from './collision-sfx';
import EnergyMesh from './EnergyMesh';
import useEnergyDrag from './use-energy-drag';

type Props = {
	energy: {
		id: string;
		color: string;
		energyColor: string;
		type: string;
		homePosition: [number, number, number];
	};

	rotato: number;

	isDraggable?: boolean;
};

export default function DraggableEnergy({
	energy,
	rotato,
	isDraggable = true,
}: Props) {
	const body = useRef<RapierRigidBody | null>(null);
	const mesh = useRef<THREE.Mesh | null>(null);
	const drag = useEnergyDrag();

	const { setIsDraggingEnergy }: any = useContext(ViewContext);

	// 1. Define your original starting position and desired rotation angle
	const initialPosition = new THREE.Vector3(...energy.homePosition);
	const rotationAngle = rotato; // Rotate 45 degrees around Y-axis

	// 2. Run the calculation ONCE during component initialization
	const { finalPosition, finalRotation } = useMemo(() => {
		// Rotate the initial vector around the world Y-axis
		const computedPos = initialPosition
			.clone()
			.applyAxisAngle(new THREE.Vector3(0, 1, 0), rotationAngle);

		// Optional: Calculate rotation so it faces the world origin (0,0,0)
		const computedRot = new THREE.Quaternion();
		const matrix = new THREE.Matrix4().lookAt(
			computedPos,
			new THREE.Vector3(0, 0, 0),
			new THREE.Vector3(0, 1, 0)
		);
		computedRot.setFromRotationMatrix(matrix);

		// Return arrays/objects compatible with @react-three/rapier props
		return {
			finalPosition: [computedPos.x, computedPos.y, computedPos.z],
			finalRotation: [
				computedRot.x,
				computedRot.y,
				computedRot.z,
				computedRot.w,
			], // Quaternion array format
		};
	}, []);

	function handlePointerDown(e: any) {
		if (!body.current || !mesh.current || !isDraggable) return;
		setIsDraggingEnergy(true);
		drag.beginDrag(e, body.current, mesh.current);
	}

	function handlePointerMove(e: any) {
		if (!body.current) return;
		drag.updateDragTarget(e);
	}

	drag.useDragFrame(body.current);

	function handlePointerUp(e: any) {
		if (!body.current) return;

		setIsDraggingEnergy(false);
		drag.endDrag(e, body.current);

		if (!mesh.current) return;

		mesh.current.updateWorldMatrix(true, false);
		const worldPosition = new THREE.Vector3();
		mesh.current.getWorldPosition(worldPosition);
	}

	// const playCount = useRef(0);
	// const timeoutRef: any = useRef(null); // Tracks the active timer

	// const MAX_PLAYS = 500;
	// const RESET_DELAY = 5; // 5 seconds

	/*
	const handleCollisionAudio = () => {
		logg('attempted to play sound', energy.type);
		if (playCount.current < MAX_PLAYS) {
			playRandomCollision(energy.type);
			playCount.current += 1;
		}

		// Clear existing timer whenever a collision happens
		if (timeoutRef.current) clearTimeout(timeoutRef.current);

		// Set a new timer to reset the count after 5 seconds of silence
		timeoutRef.current = setTimeout(() => {
			playCount.current = 0;
		}, RESET_DELAY);
	};
	*/

	const handleCollisionAudioSimple = () => {
		playRandomCollision(energy.type);
	};

	function getColor(color: string) {
		if (color === 'carbon') return 'black';

		return color;
	}

	return (
		<RigidBody
			ref={body}
			position={finalPosition as [number, number, number]}
			colliders="cuboid"
			restitution={0.35}
			friction={0.8}
			canSleep
			// Crucial integration: Rapier reads this payload automatically
			// when it triggers an intersection with your sensor field
			userData={{
				isEnergy: true,
				id: energy.id,
				energyColor: energy.energyColor,
				type: energy.type,
			}}
			onCollisionEnter={handleCollisionAudioSimple}
		>
			<group
				onPointerDown={handlePointerDown}
				onPointerMove={handlePointerMove}
				onPointerUp={handlePointerUp}
			>
				<EnergyMesh ref={mesh} color={getColor(energy.color)} />
			</group>
		</RigidBody>
	);
}
